import React, { useEffect, useRef, useState } from "react";
import LOGO from "../../assets/images/hr-logo.png";
import DefaultInput from "../../components/inputs/defaultInput";
import DefaultButton from "../../components/buttons/defaultButton";
import { _transformPayload } from "../../../utils/common";
import { forgotPassword } from "../../../services/workspace/user.service";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import { getItem, setItem } from "../../../utils/localStorage";
import { HiOutlineMail } from "react-icons/hi";
import { HiOutlineWallet } from "react-icons/hi2";
import { HiPlus } from "react-icons/hi";
import { HiOutlineArrowLeft, HiOutlineArrowRight } from "react-icons/hi";
import playstore from "../../assets/images/play-store.png";
import appstore from "../../assets/images/app-store.png";

const CAROUSEL_DOTS = 4;

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .trim()
    .required("Email is required"),
});

const ForgotPassword = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [message, setMessage] = useState("");
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [cardRotationIndex, setCardRotationIndex] = useState(0);
  const timerRef = useRef(null);

  const getRemainingTime = () => {
    const storedTime = getItem("resendTimestamp");
    if (storedTime) {
      const diff = Math.floor((parseInt(storedTime) - Date.now()) / 1000);
      return diff > 0 ? diff : 0;
    }
    return 0;
  };

  useEffect(() => {
    const remainingTime = getRemainingTime();
    if (remainingTime > 0) {
      setResendTimer(remainingTime);
    }

    if (resendTimer > 0) {
      timerRef.current = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            localStorage.removeItem("resendTimestamp");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resendTimer]);

  useEffect(() => {
    const t = setInterval(() => {
      setCardRotationIndex((i) => (i + 1) % 3);
    }, 2500);
    return () => clearInterval(t);
  }, []);

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      setIsSubmitting(true);
      setMessage(false);
      const payload = _transformPayload(values);
      forgotPassword(payload)
        .then((response) => {
          toast.success(
            "An email with your new password has been sent to your inbox. Please check and log in."
          );
          setResendTimer(45);
          const expiryTime = Date.now() + 45000;
          setMessage(true);
          setItem("resendTimestamp", expiryTime.toString());
        })
        .catch((error) => {
          toast.error(
            "We couldn't find your email. Please check the email address."
          );
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
  });

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-gray-100/80">
      {/* Left section - same as login (decorative cards + welcome + carousel) */}
      <div className="hidden lg:flex flex-1 flex-col p-8 lg:p-16 bg-gray-100">
        <div className="mb-0 flex items-center gap-2">
          <img src={LOGO} alt="Logo" className="h-12" />
        </div>

        {/* Three cards: same rotating animation as login – left/top/width/height animated */}
        <div className="relative flex-1 flex items-center justify-center min-h-[320px]">
          <div className="relative w-[400px] h-[320px]">
            {(() => {
              const SLOTS = [
                { left: 64, top: 0, width: 200, height: 130, zIndex: 10 },
                { left: 220, top: 64, width: 180, height: 140, zIndex: 20 },
                { left: 0, top: 180, width: 300, height: 120, zIndex: 0 },
              ];
              const getSlot = (cardIndex) => SLOTS[(cardIndex + cardRotationIndex) % 3];
              const baseClass = "absolute rounded-xl transition-all duration-1000 ease-in-out";
              const s0 = getSlot(0), s1 = getSlot(1), s2 = getSlot(2);
              return (
                <>
                  <div
                    className={`${baseClass} bg-white shadow-lg border border-gray-100 p-4 flex flex-col`}
                    style={{
                      left: s0.left,
                      top: s0.top,
                      width: s0.width,
                      height: s0.height,
                      zIndex: s0.zIndex,
                    }}
                  >
                    <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <HiOutlineWallet className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-3">Current Balance</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1 pb-2">$24,359</p>
                  </div>
                  <div
                    className={`${baseClass} bg-white shadow-lg border border-gray-100 p-4 flex flex-col items-center justify-center`}
                    style={{
                      left: s1.left,
                      top: s1.top,
                      width: s1.width,
                      height: s1.height,
                      zIndex: s1.zIndex,
                    }}
                  >
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#1e40af" strokeWidth="4.5" strokeDasharray="53.6 97.4" />
                        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#f97316" strokeWidth="4.5" strokeDasharray="24.35 97.4" strokeDashoffset="-53.6" />
                        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#22c55e" strokeWidth="4.5" strokeDasharray="19.45 97.4" strokeDashoffset="-77.95" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xs font-semibold text-gray-700 leading-tight">34%</span>
                        <span className="text-[10px] text-gray-500 leading-tight">Food</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`${baseClass} bg-white border-2 border-dashed border-gray-300 p-5 flex flex-col items-center justify-center`}
                    style={{
                      left: s2.left,
                      top: s2.top,
                      width: s2.width,
                      height: s2.height,
                      zIndex: s2.zIndex,
                    }}
                  >
                    <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <HiPlus className="w-6 h-6 text-blue-500" />
                    </div>
                    <p className="text-sm font-medium text-gray-600 mt-3">New transaction</p>
                    <p className="text-xs text-gray-400 mt-0.5">or upload .xls file</p>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <div className="mt-auto pt-4">
          <h1 className="text-3xl font-bold text-gray-900">Welcome back!</h1>
          <p className="text-gray-600 text-base mb-5">Get insights that help you save more</p>
        
        </div>
      </div>

      {/* Right section - Forgot password form (functionality unchanged) */}
      <div className="w-full flex-shrink-0 relative bg-white flex flex-col items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-[28rem]">
          <h1 className="text-3xl font-bold text-gray-900">Forgot Your Password?</h1>
          <p className="text-gray-600 mt-1 mb-8">
            Enter your email address and we&apos;ll help you reset your password.
          </p>

          {message ? (
            <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200">
              <p className="text-sm font-medium text-green-800">
                An email with your new password has been sent to your inbox. Please check and{" "}
                <Link to="/auth/login" className="underline font-semibold text-blue-600 hover:text-blue-700">
                  log in
                </Link>
                .
              </p>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <DefaultInput
              label="Email"
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              readOnly={isSubmitting}
              error={touched.email && errors.email ? errors.email : ""}
              placeholder="you@example.com"
              labelClass="text-gray-700"
              leftIcon={<HiOutlineMail className="w-5 h-5" />}
              classnames="border-gray-200 bg-gray-50"
              required
            />
            {resendTimer > 0 ? (
              <DefaultButton name={`Resend in ${resendTimer}s`} disabled />
            ) : (
              <DefaultButton
                variant="default"
                name="Send"
                type="submit"
                loading={isSubmitting}
                classnames="w-full justify-center"
              />
            )}
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Remember your password?{" "}
            <Link to="/auth/login" className="font-medium text-blue-600 hover:text-blue-700">
              Sign in
            </Link>
          </p>

          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-500">Explore Our App</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <div className="flex gap-3 items-center justify-center">
            <button
              type="button"
              onClick={() => window.open("https://play.google.com/store/apps/details?id=com.hr.bydo&pcampaignid=web_share", "_blank")}
            >
              <img src={playstore} alt="Google Play" className="h-10 border border-gray-200 rounded-lg hover:opacity-90" />
            </button>
            <button
              type="button"
              onClick={() => window.open("https://apps.apple.com/us/app/bydo-campus/id6741183879", "_blank")}
            >
              <img src={appstore} alt="App Store" className="h-10 border border-gray-200 rounded-lg hover:opacity-90" />
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 absolute bottom-8 left-0 right-0">
            © {new Date().getFullYear()} ALL RIGHTS RESERVED
          </p>
        </div>
      </div>

      {/* Mobile: logo at top */}
      <div className="lg:hidden absolute top-6 left-6 z-10">
          <img src={LOGO} alt="Logo" className="h-12" />
      </div>
    </div>
  );
};

export default ForgotPassword;
